import type { ReactElement } from 'react';

import { ensureDefined } from '@e-socna/utils';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';
import type { CivilityId, Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { environment } from '../../../environment';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

import { useMissionQuickAccess } from './hooks/useMissionQuickAccess.hook';

const DEFAULT_SIGNATURE_IMAGE_URL =
  `${environment.frontendUrl}/${environment.assetsUriSegment}/signatures/placeholder.png` as const;

export const useDevisSignatureGerant = (
  parameters: ResolverParameters,
): ResolverResult<ReactElement> => {
  const { civilitiesIdMap } = useReferencesContext();

  const quickAccess = useMissionQuickAccess(parameters);

  if (!quickAccess.isSuccess) {
    return {
      ...quickAccess,
      data: undefined,
    };
  }

  const {
    data: {
      consolidation: { quotationEmitterActor },
    },
  } = ensureDefined({
    data: quickAccess.data,
  });

  const getLegalRepresentatives = (): readonly {
    _id: Id;
    fullName: string;
    civilityId: Id;
    signatureImageUri?: string | null;
  }[] => {
    switch (quotationEmitterActor.__typename) {
      case 'ActorOrganization':
        return quotationEmitterActor.consolidation.legalRepresentatives.map(
          ({ person }) => person,
        );
      case 'ActorPerson':
        return [quotationEmitterActor.thirdParty];
      default:
        throw new Error('Unsupported quotation emitter actor type');
    }
  };

  const legalRepresentatives = getLegalRepresentatives();

  const title = legalRepresentatives.length > 1 ? 'Les gérants' : 'Le gérant';

  const list = legalRepresentatives.map((legalRepresentative) => {
    const { civility } = ensureDefined({
      civility: civilitiesIdMap[legalRepresentative.civilityId as CivilityId],
    });

    return (
      <div key={legalRepresentative._id} style={{ display: 'inline-block' }}>
        {civility.abbreviation} {legalRepresentative.fullName}
        <img
          alt={`Signature de ${legalRepresentative.fullName}`}
          src={
            legalRepresentative.signatureImageUri ?? DEFAULT_SIGNATURE_IMAGE_URL
          }
          style={{ maxHeight: 100 }}
        />
      </div>
    );
  });

  return {
    data: {
      generated: (
        <div className={RESOLVER_CONTENT_CLASSNAME}>
          <strong>{title}</strong>
          <br />
          {list}
        </div>
      ),
    },
    error: null,
    isSuccess: true,
    isLoading: false,
  };
};
